import React from 'react';
import { motion } from 'framer-motion';
import { Check, X, AlertTriangle, ArrowRight, Zap, Shield, TrendingUp, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
export interface VsPageLayoutProps {
  competitorName: string;
  competitorDescription: string;
  seoTitle: string;
  comparisonRows: {
    feature: string;
    mistr: 'yes' | 'no' | 'limited' | string;
    competitor: 'yes' | 'no' | 'limited' | string;
  }[];
  whyMistrWins: {
    title: string;
    desc: string;
  }[];
  cantDoList: string[];
  priceComparison: {
    competitorPrice: string;
    competitorDesc: string;
    mistrPrice: string;
    mistrDesc: string;
    note?: string;
  };
}
const renderStatus = (status: string) => {
  if (status === 'yes' || status === '✅') {
    return <div className="flex items-center gap-2 text-teal font-medium" data-id="element-905" data-mp="2vnudbhfjm9ln">
        <Check size={20} data-id="element-906" data-mp="2vlq8mx9tl6kb" /> Yes
      </div>;
  }
  if (status === 'no' || status === '❌') {
    return <div className="flex items-center gap-2 text-red-cta font-medium" data-id="element-907" data-mp="2qmn5jidehaez">
        <X size={20} data-id="element-908" data-mp="d21klry9z3o2" /> No
      </div>;
  }
  if (status.includes('Limited')) {
    return <div className="flex items-center gap-2 text-amber-400 font-medium" data-id="element-909" data-mp="i03df4li6hcq">
        <AlertTriangle size={20} data-id="element-910" data-mp="m0vkpsatec6d" /> {status}
      </div>;
  }
  if (status.includes('❌')) {
    return <div className="flex items-center gap-2 text-red-cta font-medium" data-id="element-911" data-mp="r1awiffg7bm8">
        <X size={20} data-id="element-912" data-mp="vzulypt935jc" /> {status.replace('❌', '').trim()}
      </div>;
  }
  return <span className="text-white/80" data-id="element-913" data-mp="10yfoivvz61ay">{status}</span>;
};
const icons = [<Zap size={24} data-id="element-914" data-mp="10wl21d52214r" />, <Shield size={24} data-id="element-915" data-mp="10w2vnnooi6j4" />, <TrendingUp size={24} data-id="element-916" data-mp="15pe8q6k33jjh" />, <Clock size={24} data-id="element-917" data-mp="15q764gx5kwoy" />];
export function VsPageLayout({
  competitorName,
  competitorDescription,
  seoTitle,
  comparisonRows,
  whyMistrWins,
  cantDoList,
  priceComparison
}: VsPageLayoutProps) {
  return <main className="pt-20" data-id="element-918" data-mp="1er5dn96svmca">
      {/* SEO Comment */}
      {/*
                  Title: {seoTitle}
                */}

      {/* Hero Section */}
      <section className="relative pt-24 pb-20 lg:pt-32 lg:pb-28 px-6 bg-hero-gradient overflow-hidden" data-id="element-919" data-mp="3frscubw6ybsq">
        {/* Ambient Glow Orbs */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple/20 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-920" data-mp="3aua0scmvnggi" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal/20 rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-921" data-mp="3pjochrdxe9z2" />

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: `radial-gradient(circle, white 1px, transparent 1px)`,
        backgroundSize: '40px 40px'
      }} data-id="element-922" data-mp="2bc8ofcr46yr" />

        <div className="inner-container relative z-10 text-center max-w-4xl mx-auto" data-id="element-923" data-mp="7bql09ldy32l">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-924" data-mp="2e2g650jpyjs9">
            <span className="eyebrow" data-id="element-925" data-mp="onx2rg566acj">Comparison</span>
            <h1 className="font-poppins font-extrabold text-5xl md:text-6xl leading-tight mb-6" data-id="element-926" data-mp="tjm6lvo82den">
              MINDstream vs {competitorName}.<br data-id="element-927" data-mp="evhn4phchxut" />
              <span className="text-gradient" data-id="element-928" data-mp="etwzljtg7f6g">No Contest.</span>
            </h1>
            <p className="text-lg md:text-xl text-steel mb-10 max-w-2xl mx-auto" data-id="element-929" data-mp="jt17860q54uo">
              {competitorDescription} See why business owners are switching to
              full AI business automation.
            </p>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-id="element-930" data-mp="artfy7wml4a3">
              Make the Switch <ArrowRight size={20} data-id="element-931" data-mp="x1egszx9xa1" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-purple/30 to-transparent" data-id="element-932" data-mp="1q8trr2kxiki1" />

      {/* Comparison Table */}
      <section className="section-padding bg-dark-bg relative" data-id="element-933" data-mp="3rcw06rhbborz">
        <div className="inner-container max-w-5xl relative z-10" data-id="element-934" data-mp="3rccr809n4aq3">
          <div className="text-center mb-12" data-id="element-935" data-mp="3nclwxmkwncgt">
            <h2 className="h2-title" data-id="element-936" data-mp="3n9px1sphydpq">Feature Comparison</h2>
          </div>
          <div className="bg-gradient-to-br from-white/[0.05] to-white/[0.02] backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden shadow-[0_0_40px_rgba(0,0,0,0.5)]" data-id="element-937" data-mp="3iawkc2i1aqqm">
            <div className="grid grid-cols-3 bg-white/5 border-b border-white/10 p-6" data-id="element-938" data-mp="38f39qonvl7bh">
              <div className="font-poppins font-bold text-lg" data-id="element-939" data-mp="33migppcsmy90">Feature</div>
              <div className="font-poppins font-bold text-lg text-teal flex items-center gap-2" data-id="element-940" data-mp="2zlq4cn8mkd8i">
                MISTR <Check size={20} data-id="element-941" data-mp="2unex8k9psgtz" />
              </div>
              <div className="font-poppins font-bold text-lg text-white/60" data-id="element-942" data-mp="2uopxwb7v58gv">
                {competitorName}
              </div>
            </div>
            <div className="divide-y divide-white/5" data-id="element-943" data-mp="2ko7ax7l05buq">
              {comparisonRows.map((row, i) => <div key={i} className={`grid grid-cols-3 p-6 items-center transition-colors hover:bg-white/10 ${i % 2 === 0 ? 'bg-transparent' : 'bg-white/[0.02]'}`} data-id="element-944" data-mp="2alk39xxsan11" data-mpi={i}>
                  <div className="font-medium pr-4" data-id="element-945" data-mp="2kk9kdohuw3g8">{row.feature}</div>
                  <div className="border-l-2 border-teal pl-4 py-2" data-id="element-946" data-mp="2pikv01fz6hzi">
                    {renderStatus(row.mistr)}
                  </div>
                  <div className="pl-4 py-2" data-id="element-947" data-mp="1rvuafgdl0bbr">
                    {renderStatus(row.competitor)}
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-teal/30 to-transparent" data-id="element-948" data-mp="26ix93qdn4q0o" />

      {/* Why MR Wins */}
      <section className="py-24 px-6 relative bg-white" data-id="element-949" data-mp="194kxsy8il6fy">
        <div className="max-w-4xl mx-auto" data-id="element-950" data-mp="191ypuxhpoox7">
          <div className="text-center mb-16" data-id="element-951" data-mp="1e1ula8ccf815">
            <span className="text-purple text-sm font-poppins font-bold uppercase tracking-widest" data-id="element-952" data-mp="1e0jh47ezjy9i">
              The Difference
            </span>
            <h2 className="h2-title" data-id="element-953" data-mp="113vcumgdgaj0">Why MR Wins</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-954" data-mp="1frzxvx2wcy4y">
            {whyMistrWins.map((point, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="relative bg-white border border-gray-200 rounded-3xl p-10 shadow-sm hover:shadow-xl hover:border-purple/30 transition-all group" data-id="element-955" data-mp="1at6nidiwpn90" data-mpi={idx}>
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-1 bg-gradient-to-r from-transparent via-purple to-transparent opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-956" data-mp="28g5lqsp8z390" />

                <div className="mb-8" data-id="element-957" data-mp="2b0k26r62nx08">
                  <h3 className="font-poppins font-bold text-2xl text-gray-900 mb-2" data-id="element-958" data-mp="2azihduz97w5b">
                    MR Pro
                  </h3>
                  <div className="flex items-baseline gap-2" data-id="element-959" data-mp="220ewclgj16o6">
                    <span className="text-5xl font-poppins font-extrabold text-gray-900" data-id="element-960" data-mp="221zrac6r6rmk">
                      $997
                    </span>
                    <span className="text-gray-500 font-medium" data-id="element-961" data-mp="2trurj3w5g1q0">/month</span>
                  </div>
                </div>
                <div className="text-gray-700 text-lg leading-relaxed mb-6" data-id="element-962" data-mp="2yqxlntalcnj7">
                  {point.desc}
                </div>
                <div className="flex items-center justify-center gap-4" data-id="element-963" data-mp="2kyrgt3cw22z2">
                  <div className="w-12 h-12 rounded-full bg-purple/10 border border-purple/20 flex items-center justify-center" data-id="element-964" data-mp="2p032ixsn641z">
                    <Zap size={24} className="text-purple" data-id="element-965" data-mp="3cp5qendvct4x" />
                  </div>
                  <div className="w-12 h-12 rounded-full bg-teal/10 border border-teal/20 flex items-center justify-center" data-id="element-966" data-mp="3hhytz7xlpvff">
                    <Shield size={24} className="text-teal" data-id="element-967" data-mp="2ssm45rlrgii3" />
                  </div>
                  <div className="w-12 h-12 rounded-full bg-green/10 border border-green/20 flex items-center justify-center" data-id="element-968" data-mp="2ny8256clfd4g">
                    <TrendingUp size={24} className="text-green" data-id="element-969" data-mp="2iydcf64k0tkz" />
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue/10 border border-blue/20 flex items-center justify-center" data-id="element-970" data-mp="2dziojprqceu0">
                    <Clock size={24} className="text-blue" data-id="element-971" data-mp="36qk6fymt4oh8" />
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-red-cta/30 to-transparent" data-id="element-972" data-mp="2wsw8z0xth2vd" />

      {/* What They Can't Do */}
      <section className="section-padding bg-dark-bg relative" data-id="element-973" data-mp="1qdlufsq2tft4">
        <div className="inner-container max-w-4xl relative z-10" data-id="element-974" data-mp="1lcofdp0d51vu">
          <div className="bg-gradient-to-br from-red-cta/10 to-transparent backdrop-blur-sm border border-red-cta/30 rounded-2xl p-8 md:p-12 shadow-[0_0_40px_rgba(255,0,10,0.1)]" data-id="element-975" data-mp="1lfaj1dk4ux8s">
            <h2 className="font-poppins font-bold text-3xl mb-6 flex items-center gap-3" data-id="element-976" data-mp="5rxa2v7nlmlk">
              <X className="text-red-cta" size={32} data-id="element-977" data-mp="ars06wjlu85k" /> What {competitorName}{' '}
              Can't Do
            </h2>
            <ul className="space-y-4" data-id="element-978" data-mp="flmjhyvasl44">
              {cantDoList.map((item, i) => <li key={i} className="flex items-start gap-3 text-lg text-white/90" data-id="element-979" data-mp="kkz4ee2lzi42" data-mpi={i}>
                  <span className="text-red-cta mt-1 font-bold" data-id="element-980" data-mp="tj2qpclf4iwx">•</span> {item}
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-purple/30 to-transparent" data-id="element-981" data-mp="2vbqw9sljbifv" />

      {/* Price Comparison */}
      <section className="section-padding bg-dark-bg relative overflow-hidden" data-id="element-982" data-mp="30atr6k7sw8a4">
        {/* Ambient Glow */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-purple/10 rounded-full blur-[100px] opacity-30 pointer-events-none" data-id="element-983" data-mp="38ycdz6lhtyen" />

        <div className="inner-container max-w-5xl relative z-10" data-id="element-984" data-mp="3iuphew6ubqml">
          <div className="text-center mb-16" data-id="element-985" data-mp="3ixbl2kqm1lzj">
            <h2 className="h2-title" data-id="element-986" data-mp="3mxlqnwahqadv">The Bottom Line</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8" data-id="element-987" data-mp="3rvx5klfn7b2y">
            <div className="bg-gradient-to-br from-white/[0.03] to-transparent backdrop-blur-sm border border-white/10 rounded-2xl p-8 opacity-80" data-id="element-988" data-mp="3rvdkwwyly8l8">
              <h3 className="font-poppins font-bold text-2xl mb-2 text-white/60" data-id="element-989" data-mp="5fj4b4vu5tpr">
                {competitorName}
              </h3>
              <div className="text-4xl font-bold mb-4 text-white/80" data-id="element-990" data-mp="aem8yo7wlxx7">
                {priceComparison.competitorPrice}
              </div>
              <p className="text-steel mb-6" data-id="element-991" data-mp="jftu04let29c">
                {priceComparison.competitorDesc}
              </p>
              <button className="w-full py-3 rounded-lg border border-white/20 text-white/50 font-semibold cursor-not-allowed" data-id="element-992" data-mp="23xmlzez3n49z">
                Keep {competitorName}
              </button>
            </div>
            <div className="bg-gradient-to-br from-white/[0.08] to-white/[0.02] backdrop-blur-sm border-2 border-teal rounded-2xl p-8 shadow-[0_0_40px_rgba(7,133,183,0.3)] transform md:-translate-y-4 relative overflow-hidden" data-id="element-993" data-mp="1uwocwi9t06kj">
              <div className="absolute top-0 right-0 w-32 h-32 bg-teal/20 rounded-full blur-2xl pointer-events-none" data-id="element-994" data-mp="2mn2m416vgupv" />
              <div className="inline-block bg-teal text-white text-xs font-bold uppercase tracking-wider py-1 px-3 rounded-full mb-4 relative z-10" data-id="element-995" data-mp="2inkda3sbsa3e">
                The Full Ecosystem
              </div>
              <h3 className="font-poppins font-bold text-2xl mb-2" data-id="element-996" data-mp="28wpgi5hgds3p">MR Pro</h3>
              <div className="text-4xl font-bold mb-4" data-id="element-997" data-mp="36d4wmnse59d7">
                {priceComparison.mistrPrice}
              </div>
              <p className="text-teal mb-6 font-medium" data-id="element-998" data-mp="31kbtg4ckm938">
                {priceComparison.mistrDesc}
              </p>
              <Link to="/contact" className="btn-primary w-full block text-center" data-id="element-999" data-mp="3kk7yh1fv1h6a">
                Talk to MR →
              </Link>
            </div>
          </div>
          {priceComparison.note && <p className="text-center text-steel mt-8 text-sm italic max-w-2xl mx-auto" data-id="element-1000" data-mp="3fpu9bgrsk6a2">
              * {priceComparison.note}
            </p>}
        </div>
      </section>
    </main>;
}